import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Wallet, Plus, Minus, Users, Calculator, RefreshCw 
} from 'lucide-react';
import { cn } from '../../lib/utils';
import CurrencyInput from '../../components/common/CurrencyInput';

export const PaymentSplitModal = ({
  open,
  onOpenChange,
  paymentTitle,
  totalAmount,
  members = [],
  paymentSplitData,
  setPaymentSplitData,
  formatCurrency,
  onSave,
  onCancel,
}) => {
  const [isManualMode, setIsManualMode] = useState(false);
  
  const totalParticipants = paymentSplitData?.length || 0;
  const quotaValue = totalParticipants > 0 ? totalAmount / totalParticipants : 0;
  const totalQuotas = paymentSplitData?.reduce((sum, p) => sum + p.quotas, 0) || 0;
  
  // Calculate total assigned in manual mode
  const totalManualAssigned = paymentSplitData?.reduce((sum, p) => sum + (p.manualAmount || 0), 0) || 0;
  const remainingAmount = totalAmount - totalManualAssigned;

  // Check if all quotas are distributed (quota mode) or all amount assigned (manual mode)
  const isComplete = isManualMode 
    ? Math.abs(remainingAmount) < 0.01 
    : totalQuotas === totalParticipants;

  const handleDecreaseQuota = (index) => {
    if (!paymentSplitData) return;
    if (isManualMode) {
      setIsManualMode(false);
      const resetData = paymentSplitData.map(p => ({ ...p, quotas: 0, manualAmount: 0 }));
      setPaymentSplitData(resetData);
      return;
    }
    const newData = [...paymentSplitData];
    if (newData[index].quotas > 0) {
      newData[index].quotas -= 1;
      setPaymentSplitData(newData);
    }
  };

  const handleIncreaseQuota = (index) => {
    if (!paymentSplitData) return;
    if (isManualMode) {
      setIsManualMode(false);
      const resetData = paymentSplitData.map(p => ({ ...p, quotas: 0, manualAmount: 0 }));
      setPaymentSplitData(resetData);
      return;
    }
    const newData = [...paymentSplitData];
    newData[index].quotas += 1;
    setPaymentSplitData(newData);
  };

  const handleManualAmountChange = (index, value) => {
    if (!paymentSplitData) return;
    if (!isManualMode) {
      setIsManualMode(true);
    }
    const newData = [...paymentSplitData];
    newData[index].manualAmount = value;
    newData[index].quotas = 0;
    setPaymentSplitData(newData);
  };

  const getParticipantAmount = (participant) => {
    if (isManualMode) {
      return participant.manualAmount || 0;
    }
    return participant.quotas * quotaValue;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-500" />
            Definir Rateio
          </DialogTitle>
          <DialogDescription>
            {paymentTitle && (
              <span className="block mb-2">
                Pagamento: <span className="text-white font-medium">&quot;{paymentTitle}&quot;</span>
              </span>
            )}
            Valor total: <span className="text-white font-semibold">{formatCurrency(totalAmount)}</span>
            {' | '}
            {totalParticipants} participantes
            {!isManualMode && (
              <>
                {' = '}{totalParticipants} cotas de{' '}
                <span className="text-green-400 font-semibold">{formatCurrency(quotaValue)}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg mb-2">
          <span className="text-sm text-slate-400">Modo de rateio:</span>
          <div className="flex gap-2">
            <Button
              variant={!isManualMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setIsManualMode(false);
                const resetData = paymentSplitData?.map(p => ({ ...p, quotas: 1, manualAmount: 0 })) || [];
                setPaymentSplitData(resetData);
              }}
              className="text-xs"
            >
              <Users className="w-3 h-3 mr-1" />
              Cotas iguais
            </Button>
            <Button
              variant={isManualMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setIsManualMode(true);
                const resetData = paymentSplitData?.map(p => ({ ...p, quotas: 0, manualAmount: 0 })) || [];
                setPaymentSplitData(resetData);
              }}
              className="text-xs"
            >
              <Calculator className="w-3 h-3 mr-1" />
              Valor manual
            </Button>
          </div>
        </div>

        {/* Status Bar */}
        <div className={cn(
          "text-xs p-2 rounded-lg text-center",
          isComplete ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
        )}>
          {isManualMode ? (
            <>
              Atribuído: {formatCurrency(totalManualAssigned)} / {formatCurrency(totalAmount)}
              {remainingAmount > 0 && ` (falta ${formatCurrency(remainingAmount)})`}
              {remainingAmount < 0 && ` (excede ${formatCurrency(Math.abs(remainingAmount))})`}
            </>
          ) : (
            <>Cotas distribuídas: {totalQuotas}/{totalParticipants}</>
          )}
        </div>

        {/* Participants List */}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {paymentSplitData?.map((participant, index) => {
            const participantAmount = getParticipantAmount(participant);

            return (
              <div
                key={participant.user_id}
                className="flex items-center justify-between bg-slate-800 rounded-lg p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{participant.user_name}</p>
                  {participantAmount > 0 && (
                    <p className="text-xs text-green-400">
                      {formatCurrency(participantAmount)}
                    </p>
                  )}
                </div>
                
                {isManualMode ? (
                  <div className="w-28">
                    <CurrencyInput
                      value={participant.manualAmount || 0}
                      onChange={(value) => handleManualAmountChange(index, value)}
                      className="h-8 text-sm text-right"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDecreaseQuota(index)}
                      disabled={participant.quotas === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center text-lg font-bold text-white">
                      {participant.quotas}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleIncreaseQuota(index)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            className="w-full sm:w-auto bg-green-500 hover:bg-green-600"
            disabled={!isComplete}
          >
            <Wallet className="w-4 h-4 mr-2" />
            Criar Pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSplitModal;
